import React, { useCallback, useEffect, useState } from "react";
import {
	Grid,
	Box,
	IconButton,
	TextField,
	Autocomplete,
	Checkbox,
	FormControlLabel,
	useMediaQuery,
	useTheme,
	Tooltip,
	Fab,
	Typography,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Button,
	createTheme,
} from "@mui/material";
import { Print as PrintIcon } from "@mui/icons-material";
import { connect, useSelector } from "react-redux";
import debounce from "lodash.debounce";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PATAddButton from "../../components/Buttons/PATAddButton";
import PrintsSearch3 from "../../components/PrintSearch3";
import ApplicantDialog from "./CreateFromApplicant/ApplicantDialog";
import SearchPersonDialog from "../../components/SearchPersonDialog";
import CSVLinkWrapper from "../../components/CSVLinkWrapper";
import PATClearButton from "../../components/Buttons/PATClearButton";
import FilterToolbar from "../../components/FilterToolbar";
import { useHistory } from "react-router-dom";
import { createEmployee } from "../../reducers/EmployeeReducer";
import {
	employeesView,
	entityNamesForCustomId,
	muiV4Breakpoints,
	screenNames,
} from "../../utils/constants";
import { getService } from "../../reducers/service";
import { useDispatch } from "react-redux";
import { serviceNames } from "../../utils/constants";
import { createRecordFor } from "../../utils/enums";
import { PATH_EMPLOYEE, PATH_EMPLOYEE_EDIT } from "../../utils/routeConstants";
import { notify } from "../../reducers/NotifierReducer";
import CreateFromPersonButton from "../../components/Buttons/CreateFromPersonButton";
import { areAllPropsEmpty, renderCodeOption } from "../../utils/functions";
import { getAccessPermission } from "../../reducers/PermissionsReducer";
import FilterButtons from "../../components/Buttons/FilterButtons";
import SelectFilterDialog from "../../components/SelectFilterDialog";
import SaveFilterDialog from "../../components/SaveFilterDialog";
import { setEmployeeListCurrentView } from "../../reducers/UiReducer";
import { makeStyles } from "@mui/styles";

const headers = [
	{ key: "employeeNum", label: "ID" },
	{ key: "firstName", label: "First Name" },
	{ key: "lastName", label: "Last Name" },
	{ key: "middleName", label: "Middle Name" },
	{ key: "rankInfo.description", label: "Rank" },
	{ key: "titlePositionInfo.description", label: "Title/Position" },
	{ key: "sectionInfo.description", label: "Section" },
	{ key: "departmentInfo.description", label: "Department/Division" },
];

const columnsForPrint = headers.map((h) => ({
	field: h.key,
	headerName: h.label,
	editable: false,
	flex: 1,
}));

const filterFieldOptions = [{ label: "City", value: "City" }];

const useStyles = makeStyles((theme) => {
	return {
		currentViewText: {
			whiteSpace: "nowrap",
			overflow: "hidden",
			textOverflow: "ellipsis",
		},
		viewText: {
			position: "absolute",
			top: "-10px",
			left: "20px",
			backgroundColor: theme.palette.primary.main,
			color: "white",
			borderRadius: "10px",
			padding: "0 8px",
			fontSize: "14px",
			fontWeight: "500",
		},
	};
});

const EnhancedTableToolbar = ({
	employees,
	keyboard,
	onAddClick,
	onFilterChange,
	filters,
	employmentOptions,
	departmentOptions,
	titlePositionOptions,
	rankOptions,
	sectionOptions,
	countyOptions,
	genderOptions,
	raceOptions,
	ethnicityOptions,
	contactInfoOptions,
	tagTypeOptions,
	suffixOptions,
	resetFilters,
}) => {
	const history = useHistory();
	const dispatch = useDispatch();

	const outerTheme = useTheme();
	const theme = createTheme({
		...outerTheme,
		breakpoints: muiV4Breakpoints,
	});
	const classes = useStyles();
	const userAgency = useSelector((state) => state.user.userAgency);
	const dictionary = useSelector((state) => state.dictionary.dictionary);
	const currentView = useSelector(
		(state) => state.ui.employeeListCurrentView
	);
	const isSmall = useMediaQuery(outerTheme.breakpoints.down("md"));
	const isXtraLarge = useMediaQuery(theme.breakpoints.down("xl"));
	const [printFlag, setPrintFlag] = useState(false);
	const [kbdKey, setKbdKey] = useState(null);
	const [localFilters, setLocalFilters] = useState(filters);

	const [openApplicantDialog, setOpenApplicantDialog] = useState(false);
	const [openSearchPersonDialog, setOpenSearchPersonDialog] = useState(false);
	const [openSelectFilterDialog, setOpenSelectFilterDialog] = useState(false);
	const [openSaveFilterDialog, setOpenSaveFilterDialog] = useState(false);

	const isFilterEmpty = areAllPropsEmpty(localFilters);
	const employeePermission = getAccessPermission(
		"employees",
		"Employee Record",
		"any"
	);
	const canCreateEmployees = !!employeePermission?.Create;

	const handleOpenApplicantDialog = () => {
		setOpenApplicantDialog(true);
	};

	const handleCreateFromPerson = async (selectedPerson) => {
		if (!selectedPerson) {
			dispatch(notify("No person selected", "warning"));
			return;
		}

		const generateCustomId = async () => {
			const data = {
				EntityName: entityNamesForCustomId.personnelEmployeeId,
				agencyID: userAgency,
			};
			const service = getService(serviceNames.customId);
			service.timeout = 20000;
			const id = await service.create(data);
			return id;
		};

		const service = await getService(serviceNames.searchPeople);
		const res = await service.get(selectedPerson.ptsPersonID);
		const generatedId = await generateCustomId();
		if (generatedId?.includes("Agency ID does not match")) {
			dispatch(
				notify(
					"Unable to create employee record. No custom Employee ID has been configured for this agency.",
					"warning"
				)
			);
			return;
		}

		let address = "";
		let rZip = "";
		let rState = "";
		let rCity = "";
		let race = "";
		let gender = "";

		if (res) {
			try {
				address = res.FullAddressText || null;
				rZip = res.PostalCode || null;
				rState = res.State || null;
				rCity = res.City || null;
			} catch (e) {
				console.error("Invalid address format", e);
			}
		}

		if (selectedPerson.Race) {
			const item = dictionary.codeRace.find(
				(r) => r.Code === selectedPerson.Race
			);
			race = item?.Code || selectedPerson.Race;
		}

		if (selectedPerson.Sex) {
			const item = dictionary.codeSex.find(
				(r) => r.Code === selectedPerson.Sex
			);
			gender = item?.Code || selectedPerson.Sex;
		}
		const employeeData = {
			firstName: selectedPerson.FirstName?.trim() || null,
			middleName: selectedPerson.MiddleName?.trim() || null,
			lastName: selectedPerson.LastName?.trim() || null,
			employeeNum: generatedId,
			phoneNo: res?.PhoneNumber || null,
			email: selectedPerson?.Email ||res?.Email || null,
			workPhoneNum:
				res?.WorkPhoneNumber || res?.BusinessPhoneNumber || null,
			suffix: selectedPerson.Suffix?.trim() || null,
			ssn:
				selectedPerson.SSN?.trim().length > 9
					? null
					: selectedPerson.SSN?.trim(),
			race: race.trim() || null,
			gender: gender.trim() || null,
			dob: selectedPerson.BirthDate,
			bloodType: selectedPerson.BloodType || null,
			notes: selectedPerson.Notes || null,
			maritalStatus: selectedPerson.MaritalStatus || null,
			address: address || null,
			rZip: rZip || null,
			rCity: rCity || null,
			rState: rState || null,
			ptsPersonId: selectedPerson.ptsPersonID || null,
			agencyId: userAgency || null,
		};
		try {
			dispatch(createEmployee(employeeData)).then((result) => {
				if (result?.duplicate) {
					dispatch(
						notify(
							`Employee Number "${employeeData.employeeNum}" already exists.`,
							"warning"
						)
					);
					return;
				}

				if (result?.id) {
					history.push(`${PATH_EMPLOYEE_EDIT}${result.id}`);
				} else {
					history.push(`${PATH_EMPLOYEE}`);
				}
			});
		} catch (error) {
			dispatch(notify("Failed to create employee", "error"));
		}
	};

	useEffect(() => {
		setLocalFilters(filters);
	}, [filters]);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debouncedFilterChange = useCallback(
		debounce((newFilters) => {
			onFilterChange(newFilters);
		}, 500),
		[onFilterChange]
	);

	const handleFilterChange = useCallback(
		(field, value) => {
			const updatedFilters = { ...localFilters, [field]: value };
			setLocalFilters(updatedFilters);
			debouncedFilterChange(updatedFilters);
		},
		[debouncedFilterChange, localFilters]
	);

	useEffect(() => {
		const subscription = keyboard.subject.subscribe((key) => {
			const validKeys = ["F5", "Ctrl+KeyP", "Ctrl+KeyL"];
			if (validKeys.includes(key)) setKbdKey(key);
		});
		return () => subscription.unsubscribe();
	}, [keyboard]);

	const handlePrintClick = () => {
		setPrintFlag(true);
		setTimeout(() => setPrintFlag(false), 1000);
	};

	const renderFilterFields = (fields) =>
		fields.map(
			({ type, label, field, options, xs = 2, multiselect = true }) => (
				<Grid item xs={isSmall ? 12 : xs} key={field}>
					{type === "text" ? (
						<TextField
							label={label}
							value={localFilters[field] || ""}
							onChange={(e) =>
								handleFilterChange(field, e.target.value)
							}
							fullWidth
							variant="outlined"
							size="small"
						/>
					) : (
						<Autocomplete
							multiple={multiselect}
							options={options || []}
							value={
								multiselect
									? localFilters[field] || []
									: localFilters[field] || null
							}
							onChange={(_, value) =>
								handleFilterChange(field, value)
							}
							getOptionLabel={(option) => option?.label || ""}
							isOptionEqualToValue={(o, v) =>
								o?.value === v?.value
							}
							renderOption={renderCodeOption}
							renderInput={(params) => (
								<TextField
									{...params}
									label={label}
									variant="outlined"
									size="small"
								/>
							)}
							fullWidth
							sx={
								multiselect
									? {
											"& .MuiAutocomplete-inputRoot": {
												flexWrap: "nowrap",
												maxHeight: "40px",
											},
											"& .MuiAutocomplete-tag": {
												maxWidth: "100%",
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
											},
											"& .MuiAutocomplete-endAdornment": {
												alignSelf: "flex-start",
											},
									  }
									: {}
							}
						/>
					)}
				</Grid>
			)
		);

	const renderExactMatchAndClearFilter = () => {
		return (
			<>
				<Grid item>
					<FormControlLabel
						control={
							<Checkbox
								checked={!!localFilters.exactMatch}
								onChange={(e) =>
									handleFilterChange(
										"exactMatch",
										e.target.checked
									)
								}
							/>
						}
						label="Exact Match"
					/>
				</Grid>
				<Grid item>
					<PATClearButton
						title="Clear Filters"
						onClick={resetFilters}
					/>
				</Grid>
				<FilterButtons
					onSelectFilterClick={() => setOpenSelectFilterDialog(true)}
					onSaveFilterClick={() => setOpenSaveFilterDialog(true)}
					isFilterEmpty={isFilterEmpty}
				/>

				{/* {renderCreateFromApplicant(!isSmall)} */}
			</>
		);
	};

	const renderCreateFromApplicant = (condition = false) => {
		return condition ? (
			<Grid item>
				<Tooltip title={"Import from applicant"}>
					<Fab
						size="small"
						color="secondary"
						aria-label="add"
						onClick={handleOpenApplicantDialog}
					>
						<PersonAddIcon />
					</Fab>
				</Tooltip>
			</Grid>
		) : (
			<></>
		);
	};

	const renderCreateFromPerson = (condition = false) => {
		return condition ? (
			<Grid item>
				<CreateFromPersonButton
					tooltipText="Create from person record"
					onClick={() => setOpenSearchPersonDialog(true)}
				/>
			</Grid>
		) : null;
	};

	const renderOptions = () => {
		return (
			<>
				{isSmall ? (
					<Box
						sx={{
							display: "flex",
						}}
					>
						<Grid item>
							<CSVLinkWrapper
								data={employees}
								headers={headers}
								filename="employees.csv"
							/>
						</Grid>
						<Grid item>
							<IconButton
								onClick={handlePrintClick}
								color="primary"
								disabled={!employees?.length}
							>
								<PrintIcon />
							</IconButton>
						</Grid>
						{/* {renderCreateFromApplicant(isSmall)} */}
					</Box>
				) : (
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							width: "100%",
							alignItems: "center",
						}}
					>
						<Grid item>
							<CSVLinkWrapper
								data={employees}
								headers={headers}
								filename="employees.csv"
							/>
						</Grid>
						<Grid item>
							<IconButton
								onClick={handlePrintClick}
								color="primary"
								disabled={!employees?.length}
							>
								<PrintIcon />
							</IconButton>
						</Grid>
						{/* {renderCreateFromApplicant(isSmall)} */}
					</Box>
				)}
			</>
		);
	};

	const renderAddButton = () => {
		return (
			<>
				{isSmall ? (
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<PATAddButton
							tooltipText="Add Employee"
							onClick={onAddClick}
						/>
						{renderCreateFromApplicant(true)}
						{renderCreateFromPerson(true)}
					</Box>
				) : (
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							gap: 1,
						}}
					>
						<PATAddButton
							tooltipText="Add Employee"
							onClick={onAddClick}
						/>
						{renderCreateFromApplicant(true)}
						{renderCreateFromPerson(true)}
					</Box>
				)}
			</>
		);
	};

	const filterFields1 = [
		{ type: "text", label: "ID", field: "employeeNum", xs: 1 },
		{ type: "text", label: "SSN", field: "ssn", xs: 2 },
		{ type: "text", label: "Last Name", field: "lastName", xs: 2 },
		{ type: "text", label: "First Name", field: "firstName", xs: 2 },
		{ type: "text", label: "Middle Name", field: "middleName", xs: 2 },
		{
			type: "autocomplete",
			label: "Status",
			field: "status",
			options: employmentOptions,
			xs: 1.5,
		},
		{
			type: "text",
			label: "Separation Code",
			field: "separationCode",
			xs: 1.5,
		},
	];

	const filterFields2 = [
		{
			type: "autocomplete",
			label: "Department",
			field: "department",
			options: departmentOptions,
			xs: 1.7,
		},
		{
			type: "autocomplete",
			label: "Title/Position",
			field: "titlePosition",
			options: titlePositionOptions,
			xs: 1.7,
		},
		{
			type: "autocomplete",
			label: "Rank",
			field: "rank",
			options: rankOptions,
			xs: 1.7,
		},
		{
			type: "autocomplete",
			label: "Section",
			field: "section",
			options: sectionOptions,
			xs: 1.5,
		},
		{
			type: "text",
			label: "Cell No.",
			field: "cellNo",
			xs: 1.7,
		},
		{
			type: "autocomplete",
			label: "Assigned County",
			field: "assignedCounty",
			options: countyOptions,
			xs: 1.7,
		},
		{
			type: "autocomplete",
			label: "Tags",
			field: "tagTypes",
			options: tagTypeOptions,
			xs: 2,
		},
	];

	const filterFields3 = [
		{
			type: "autocomplete",
			label: "Filter Field",
			field: "filterField",
			options: filterFieldOptions,
			xs: 2,
			multiselect: false,
		},
		{
			type: "text",
			label: "Includes the value",
			field: "filterValue",
			xs: 4,
		},
	];

	const saveEmployeeView = async (view) => {
		try {
			const filterValues = JSON.stringify({
				view: view,
			});
			const service = getService(serviceNames.perFilters);
			const result = await service.create({
				screenName: screenNames.employeesView,
				name: screenNames.employeesView,
				filterValues: filterValues,
			});
			if (result) dispatch(setEmployeeListCurrentView(view));
		} catch (error) {
			console.error("Error fetching filters:", error);
		}
	};

	const handleCurrentViewChange = (view) => {
		if (currentView === view) return;
		saveEmployeeView(view);
	};

	const renderViewDropdown = () => {
		return (
			<FormControl
				variant="outlined"
				size="small"
				style={{ width: "200px", color: "white" }}
			>
				<InputLabel id={`current-view`}>Current View</InputLabel>
				<Select
					label="current-view"
					labelId="current-view"
					id="current-view"
					value={currentView}
					onChange={(e) => handleCurrentViewChange(e.target.value)}
				>
					<MenuItem value={employeesView.employeeGridView}>
						{" "}
						Employees Grid View
					</MenuItem>
					<MenuItem value={employeesView.employeeCardView}>
						{" "}
						Employees Card View
					</MenuItem>
				</Select>
			</FormControl>
		);
	};

	return (
		<>
			<Typography sx={{ fontSize: 20 }}>Employees</Typography>
			<FilterToolbar
				isSmall={isSmall}
				canCreate={canCreateEmployees}
				leftSection={
					!isSmall && (
						<Box
							sx={{
								border: (theme) =>
									`1px solid ${theme.palette.primary.main}`,
								borderRadius: "10px",
								padding: 2,
								height: "100%",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								marginLeft: 1,
							}}
						>
							{renderAddButton()}
						</Box>
					)
				}
				middleSection={
					<Box
						sx={{
							border: (theme) =>
								`1px solid ${theme.palette.primary.main}`,
							borderRadius: "10px",
							padding: 2,
							maxHeight: { xs: "25vh", md: "40vh" },
							overflowY: "auto",
							marginLeft: canCreateEmployees ? "" : 1.5,
						}}
					>
						<Grid container spacing={1}>
							{renderFilterFields([
								...filterFields1,
								...filterFields2,
								...filterFields3,
							])}
							{renderExactMatchAndClearFilter()}
						</Grid>
					</Box>
				}
				rightSection={
					!isSmall && (
						<div style={{ display: "flex", height: "100%" }}>
							<Box
								sx={{
									border: (theme) =>
										`1px solid ${theme.palette.primary.main}`,
									borderRadius: "10px",
									padding: 2,
									display: "flex",
									flexDirection: "column",
									alignItems: "flex-start",
									gap: 1,
									height: "100%",
									justifyContent: "center",
									marginRight: 1,
								}}
							>
								{renderOptions()}
							</Box>
							{!isXtraLarge && (
								<Box
									sx={{
										position: "relative",
										paddingTop: "20px",
										border: (theme) =>
											`1px solid ${theme.palette.primary.main}`,
										borderRadius: "10px",
										padding: 2,
										display: "flex",
										flexDirection: "column",
										alignItems: "flex-start",
										gap: 1,
										height: "100%",
										justifyContent: "center",
										marginRight: 1,
									}}
								>
									<Box className={classes.viewText}>
										Current View
									</Box>

									<Box>
										<Grid
											container
											direction="column"
											justifyContent="center"
											alignItems="center"
										>
											<Button
												style={{
													color:
														currentView ===
														employeesView.employeeGridView
															? "#1e8dfa"
															: theme.palette
																	.mode ===
															  "dark"
															? "#FFF"
															: "#000",
													fontSize: "15px",
													padding: "5px",
												}}
												className={
													classes.currentViewText
												}
												fullWidth
												onClick={() =>
													handleCurrentViewChange(
														employeesView.employeeGridView
													)
												}
											>
												Employees Grid View
											</Button>
											<Button
												style={{
													color:
														currentView ===
														employeesView.employeeCardView
															? "#1e8dfa"
															: theme.palette
																	.mode ===
															  "dark"
															? "#FFF"
															: "#000",
													fontSize: "15px",
													padding: "5px ",
													marginTop: "10px",
												}}
												className={
													classes.currentViewText
												}
												fullWidth
												onClick={() =>
													handleCurrentViewChange(
														employeesView.employeeCardView
													)
												}
											>
												Employees Card View
											</Button>
										</Grid>
									</Box>
								</Box>
							)}
						</div>
					)
				}
				combinedActions={
					<Box
						sx={{
							mt: 2,
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							gap: 2,
							flexWrap: "wrap",
						}}
					>
						{renderAddButton()}
						{renderOptions()}
					</Box>
				}
				leftGridProps={{ xs: 12, md: 1 }}
				middleGridProps={{ xs: 12, md: 10 }}
				rightGridProps={{ xs: 12, md: 1 }}
			/>

			{isXtraLarge && (
				<Grid
					container
					wrap="nowrap"
					spacing={2}
					className="pt-2 d-flex justify-content-center align-items-center"
					marginTop={1.5}
				>
					{renderViewDropdown()}
				</Grid>
			)}

			<PrintsSearch3
				title="Employees"
				data={employees}
				cols={columnsForPrint}
				kbdKey={kbdKey}
				print={printFlag}
			/>
			<ApplicantDialog
				open={openApplicantDialog}
				onClose={() => setOpenApplicantDialog(false)}
			/>
			<SearchPersonDialog
				open={openSearchPersonDialog}
				onClose={() => setOpenSearchPersonDialog(false)}
				onConfirm={handleCreateFromPerson}
				title="Create Employee from Person"
				confirmMessage={(p) =>
					`Create "${p?.FirstName} ${p?.LastName}" as an employee?`
				}
				createFor={createRecordFor.Employee}
				genderOptions={genderOptions}
				raceOptions={raceOptions}
				ethnicityOptions={ethnicityOptions}
				contactInfoOptions={contactInfoOptions}
				suffixOptions={suffixOptions}
			/>

			<SelectFilterDialog
				open={openSelectFilterDialog}
				onClose={() => setOpenSelectFilterDialog(false)}
				screenName={screenNames.employees}
				onFilterChange={onFilterChange}
			/>

			<SaveFilterDialog
				open={openSaveFilterDialog}
				onClose={() => setOpenSaveFilterDialog(false)}
				screenName={screenNames.employees}
				filters={localFilters}
			/>
		</>
	);
};

const mapStateToProps = (state, ownProps) => ({
	keyboard: state.keyboard,
	...ownProps,
});

export default connect(mapStateToProps)(EnhancedTableToolbar);
